import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Company } from '../interfaces/company';

const COMPANY = makeStateKey<Company>('company');

/// The company behind the shop - legal name, registered office - read once from
/// /api/Site/Company, the one place those facts are stated (CompanyIdentity_Spec.md). The server
/// render asks for it and carries the answer in the page, so the facts are in the HTML a crawler
/// reads and the browser does not ask again.
///
/// There is no fallback copy of any legal fact: if the call fails this gives null, and a page shows
/// the brand and leaves the legal rows out - a stale address is worse than a missing one.
@Injectable({
    providedIn: 'root'
})
export class CompanyService {
    readonly company$: Observable<Company | null>;

    constructor(private http: HttpClient, state: TransferState, @Inject(PLATFORM_ID) platformId: any) {
        const carried = state.get<Company>(COMPANY, null);

        this.company$ = (carried
            ? of(carried)
            : http.get<Company>(environment.apiUrl + '/api/Site/Company').pipe(
                tap(company => {
                    if (isPlatformServer(platformId) && company) {
                        state.set(COMPANY, company);
                    }
                }),
                catchError(() => of(null))))
            .pipe(shareReplay(1));
    }

    /// The shop's mailbox, for the legal notice's "Show email address" only. It is in no page and not in the company
    /// above (which every page's HTML carries), because an address on a page is harvested for spam: it is asked for
    /// when a visitor presses the button, and never on the server render.
    email(): Observable<string> {
        return this.http.post<{ Email: string }>(environment.apiUrl + '/api/Site/Email', {}).pipe(map(r => (r && r.Email) || ''));
    }
}
